import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getGradientBg = (name) => {
  const colors = [
    "from-blue-600 to-indigo-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
    "from-purple-600 to-fuchsia-600",
    "from-cyan-500 to-blue-600"
  ];
  let hash = 0;
  const str = name || "P";
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const MessageCard = ({ name, role, imageSrc, children }) => {
  const hasImage = imageSrc && 
                   imageSrc !== "/images/placeholder-user.svg" && 
                   !imageSrc.toLowerCase().includes("placeholder") && 
                   !imageSrc.toLowerCase().includes("default-user");
  
  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12 border border-slate-100">
      <div className="md:flex">
        <div className="md:w-1/3 bg-slate-50 flex flex-col items-center justify-center p-8 border-r border-slate-100">
          <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-lg mb-4 flex items-center justify-center relative bg-white">
            {hasImage ? (
              <img 
                src={imageSrc} 
                alt={name} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  if (e.currentTarget.nextSibling) {
                    e.currentTarget.nextSibling.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <div 
              className={`w-full h-full bg-gradient-to-br ${getGradientBg(name)} flex items-center justify-center text-white relative`}
              style={{ display: hasImage ? 'none' : 'flex' }}
            >
              <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-full w-24 h-24 flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-105">
                <span className="text-4xl font-black text-white drop-shadow-md">{getInitials(name)}</span>
              </div>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 text-center">{name}</h3>
          <p className="text-blue-600 font-bold text-sm tracking-wider uppercase mt-2">{role}</p>
        </div>
        <div className="md:w-2/3 p-8 md:p-12 flex flex-col justify-center">
          <div className="prose max-w-none text-slate-600 leading-relaxed space-y-4 text-justify">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_HERO = {
  heading: "Leadership Messages",
  subheading:
    "Visionary words from the leaders driving the Para Sports movement in Andhra Pradesh.",
};

const DEFAULT_LEADERS = [
  {
    name: "Gonuguntla Koteswara Rao",
    role: "President",
    imageSrc: "/images/placeholder-user.svg",
    highlightIndexes: [3],
    paragraphs: [
      "When we started the Para Sports Association of Andhra Pradesh in 2015, we had little more than determination. No proper infrastructure, no steady funding, and very few people believed in the dream of building a Para sports movement in a newly formed state. Yet, we persisted.",
      "Today, we have over 450 national medals, 40 international medals, six Asian Para Games participants — three silver medalists — and two members representing us at the Paris Paralympics. These numbers are not the result of wealth or comfort; they are the result of sacrifice, collective will, and the belief that ability knows no barriers.",
      "But behind every medal lies a story of struggle: training with outdated equipment, traveling on borrowed funds, competing without the assurance of stable financial backing. Our athletes and coaches have often gone far beyond their means — and still, they have delivered.",
      "This is not the end — it is the beginning of a larger mission. We will expand to every district, train more coaches, and set our sights on Paralympic podium finishes. Our athletes have proved their ability; now we must give them the resources to match their dreams.",
    ],
  },
  {
    name: "V. Rama Swamy",
    role: "Secretary",
    imageSrc: "/images/placeholder-user.svg",
    highlightIndexes: [4],
    paragraphs: [
      "For the past decade, I have seen what it means to build something from nothing. As Secretary, I have negotiated travel at midnight, arranged borrowed kits for athletes, and ensured that our Para athletes never missed a championship — even when finances said otherwise.",
      "We began with a handful of participants and minimal infrastructure. Today, we celebrate a robust medal tally, international exposure, and pathways to the Paralympics. But behind each of those medals are unseen efforts — arranging last-minute travel, negotiating sponsorships, conducting classification camps, supporting athletes’ families, and ensuring that no one is left behind due to financial or social barriers. Each victory has been hard-earned, often without the cushion that mainstream sports enjoy.",
      "Looking ahead, our focus is on sustainability and fairness: transparent selection processes, early talent scouting, athlete insurance, and post-retirement support. My vision is simple — no divyang athlete should have to fight two battles: one on the field and one for their basic rights.",
      "As Secretary, I promise to continue working with transparency, dedication, and passion to create a system that not only produces champions but also protects their future — through career support, education, and welfare schemes. Our athletes are not just medal winners; they are symbols of what Andhra Pradesh stands for — resilience, inclusivity, and pride.",
      "We now stand at a decisive point: the talent is proven, the system is built, the credibility is earned — what we need is partners who believe in this cause as much as we do.",
    ],
  },
];

const normalizeKey = (value) => String(value || "").trim().toLowerCase();

const parseParagraphs = (bodyText) => {
  const raw = String(bodyText || "").replace(/\r\n/g, "\n").trim();
  if (!raw) return [];

  const byDoubleBreak = raw
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (byDoubleBreak.length > 1) return byDoubleBreak;

  return raw
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
};

const Messages = () => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await API.fetchAboutSections("messages");
        if (!mounted) return;
        setSections(Array.isArray(data) ? data : []);
      } catch (_error) {
        if (!mounted) return;
        setSections([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const sectionMap = useMemo(() => {
    const map = {};
    (Array.isArray(sections) ? sections : [])
      .filter((section) => section?.is_active !== false)
      .forEach((section) => {
        map[normalizeKey(section.section_key)] = section;
      });
    return map;
  }, [sections]);

  const sortedSections = useMemo(
    () =>
      (Array.isArray(sections) ? sections : [])
        .filter((section) => section?.is_active !== false)
        .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)),
    [sections]
  );

  const heroSection = sectionMap["messages_hero"] || null;
  const heroHeading = (heroSection?.heading || "").trim() || DEFAULT_HERO.heading;
  const heroSubheading = (heroSection?.subheading || "").trim() || DEFAULT_HERO.subheading;
  const heroImage = heroSection?.image_path
    ? resolveAssetUrl(heroSection.image_path, { folder: "page-sections", defaultPath: "" })
    : "";

  const dynamicLeaders = useMemo(() => {
    const rows = sortedSections
      .filter((section) => {
        const key = normalizeKey(section.section_key);
        return key.startsWith("messages_leader_") || key.startsWith("messages_card_");
      })
      .map((section) => ({
        name: (section.heading || "").trim(),
        role: (section.subheading || "").trim(),
        imageSrc: section.image_path
          ? resolveAssetUrl(section.image_path, {
              folder: "page-sections",
              defaultPath: "/images/placeholder-user.svg",
            })
          : "/images/placeholder-user.svg",
        paragraphs: parseParagraphs(section.body),
        highlightIndexes: [],
      }))
      .filter((row) => (row.name || row.role || row.paragraphs.length > 0) && row.name !== "Leader Name" && row.name !== "Leader Name ");

    return rows.length ? rows : DEFAULT_LEADERS;
  }, [sortedSections]);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative text-white py-20 px-6 text-center">
        {heroImage ? (
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Leadership hero"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/image-fallback.svg";
              }}
            />
          </div>
        ) : null}
        <div className={`absolute inset-0 ${heroImage ? "bg-blue-900/70" : "bg-blue-900"}`} />
        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{heroHeading}</h1>
          <p className="text-xl opacity-90">
            {heroSubheading}
          </p>
        </div>
      </section>

      {/* Messages Content */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {dynamicLeaders.map((leader, leaderIndex) => (
            <MessageCard
              key={`${leader.name || "leader"}-${leader.role || "role"}-${leaderIndex}`}
              name={leader.name || "Leader"}
              role={leader.role || ""}
              imageSrc={leader.imageSrc}
            >
              {(leader.paragraphs || []).map((paragraph, idx) => (
                <p
                  key={`${leader.name || "leader"}-p-${idx}`}
                  className={
                    (leader.highlightIndexes || []).includes(idx)
                      ? "font-semibold text-blue-800"
                      : undefined
                  }
                >
                  {paragraph}
                </p>
              ))}
            </MessageCard>
          ))}
        </div>
      </section>

      <PageSectionsRenderer
        pageKey="messages"
        className="pt-0"
        excludeKeys={["messages_hero"]}
        excludeKeyPrefixes={["messages_leader_", "messages_card_"]}
      />

      <Footer />
    </div>
  );
};

export default Messages;

